import type { CallStatusResourceResponseListResponseApi } from '@ingenico/api-sdk-device-management-private';
import type { BusinessServiceRequest } from '@ingenico/react-section-utils';
import { useCurrentUser } from '@ingenico/react-section-utils';
import type { DeviceChangesCallLog } from '@ingenico/react-ui';
import { useUserDateTimeFormat } from '@ingenico/react-utils/hooks';
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { camelCase, toArray, toString } from 'lodash-es';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeviceManagementHostInfo } from './useDeviceManagementHostInfo.hook';
import { useDeviceManagementSectionRoutePrefixHook } from './useDeviceManagementSectionRoutePrefix.hook';

export function useGenerateProvisioningTargetDeviceCallLogs(
  { data: responseData }: BusinessServiceRequest<CallStatusResourceResponseListResponseApi>,
  virtualDeviceId = '',
): DeviceChangesCallLog[] {
  const currentUser = useCurrentUser();
  const { getUserDateTimeFormat } = useUserDateTimeFormat(currentUser?.dateTimeFormat);
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.CALL_LOGS]);
  const isStandaloneApp = useDeviceManagementHostInfo().kind === 'standalone';
  const deviceManagementRoutePrefix = useDeviceManagementSectionRoutePrefixHook();

  return useMemo(
    () =>
      toArray(
        responseData?.data?.map(({ callDateTime, status, statusSummary, callId }) => {
          const formattedCallDate = getUserDateTimeFormat({ instant: callDateTime?.toString() });
          const standaloneCallDateUrl = `${deviceManagementRoutePrefix}/virtual-devices/${virtualDeviceId}/calls/${callId}`;
          const callDate = (
            isStandaloneApp ? { date: formattedCallDate, link: standaloneCallDateUrl } : formattedCallDate
          ) as DeviceChangesCallLog['callDate'];

          return {
            reason: t(`values.callReason.${camelCase(toString(statusSummary))}`, {
              ns: ALL_LNGS_NAMESPACES.CALL_LOGS,
            }),
            callDate,
            status,
            callId,
            virtualDeviceId,
          };
        }),
      ),
    [responseData?.data, getUserDateTimeFormat, deviceManagementRoutePrefix, isStandaloneApp, t, virtualDeviceId],
  );
}
